'use client';

import React, { useRef, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './IndustriesCarousel.module.css';

interface Industry {
  key: string;
  name: string;
  image: string;
  href: string;
  alt: string;
}

interface IndustriesCarouselProps {
  industries: Industry[];
  variant?: 'double-row' | 'single-row';
  autoPlaySpeed?: number;
  /** When false, cards render as plain divs instead of links (reduces crawlable link count) */
  linkCards?: boolean;
}

interface CarouselIndustry extends Industry {
  isClone: boolean;
  cloneSide?: 'before' | 'after';
}

/**
 * IndustriesCarousel - High-Performance Infinite Carousel
 * 
 * Features:
 * - Pure CSS animations (GPU-accelerated)
 * - Automatic smooth scrolling
 * - Manual scrolling support
 * - Infinite loop (seamless)
 * - Pause on hover
 * - SEO-optimized with proper alt texts per language
 * - Accessibility compliant (ARIA labels, keyboard nav)
 * - Performance optimized (lazy loading, backface-visibility)
 * - Responsive design
 * 
 * Used in: Homepage, Solution pages (kiosk, QR, tablet, webshop)
 */
export const IndustriesCarousel: React.FC<IndustriesCarouselProps> = ({
  industries,
  variant = 'single-row',
  autoPlaySpeed = 60, // seconds for one complete loop
  linkCards = true,
}) => {
  const row1Ref = useRef<HTMLDivElement>(null);
  const row2Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = (wrapper: HTMLDivElement, track: HTMLElement) => {
      let scrollTimeout: NodeJS.Timeout;
      
      const onScroll = () => {
        // Pause animation
        track.style.animationPlayState = 'paused';
        
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          // Resume animation
          track.style.animationPlayState = 'running';
        }, 1500);
        
        // Infinite loop for manual scrolling
        const scrollPos = wrapper.scrollLeft;
        const itemWidth = wrapper.scrollWidth / 3; // We have 3 copies
        
        // If scrolled past 2/3, reset to 1/3
        if (scrollPos >= itemWidth * 2) {
          wrapper.scrollLeft = itemWidth;
        }
        // If scrolled before 1/3, reset to 1/3
        else if (scrollPos <= itemWidth * 0.1) {
          wrapper.scrollLeft = itemWidth;
        }
      };

      wrapper.addEventListener('scroll', onScroll, { passive: true });
      
      // Set initial position after images load
      requestAnimationFrame(() => {
        const itemWidth = wrapper.scrollWidth / 3;
        wrapper.scrollLeft = itemWidth;
      });
      
      return () => {
        wrapper.removeEventListener('scroll', onScroll);
        clearTimeout(scrollTimeout);
      };
    };

    const wrapper1 = row1Ref.current;
    const track1 = wrapper1?.querySelector(`.${styles.carouselTrack}`) as HTMLElement;
    const cleanup1 = wrapper1 && track1 ? handleScroll(wrapper1, track1) : undefined;

    const wrapper2 = row2Ref.current;
    const track2 = wrapper2?.querySelector(`.${styles.carouselTrack}`) as HTMLElement;
    const cleanup2 = wrapper2 && track2 ? handleScroll(wrapper2, track2) : undefined;

    return () => {
      cleanup1?.();
      cleanup2?.();
    };
  }, []);

  // Split for double-row
  const splitIndex = Math.ceil(industries.length / 2);
  const row1Industries = variant === 'double-row' 
    ? industries.slice(0, splitIndex) 
    : industries;
  const row2Industries = variant === 'double-row' 
    ? industries.slice(splitIndex) 
    : [];

  const buildLoopItems = (source: Industry[]): CarouselIndustry[] => [
    ...source.map((industry) => ({ ...industry, isClone: true, cloneSide: 'before' as const })),
    ...source.map((industry) => ({ ...industry, isClone: false })),
    ...source.map((industry) => ({ ...industry, isClone: true, cloneSide: 'after' as const })),
  ];

  // Keep three visual copies for the seamless scroll, but only the center copy is interactive.
  const row1Items = buildLoopItems(row1Industries);
  const row2Items = buildLoopItems(row2Industries);
  const singleRowItems = buildLoopItems(industries);

  const renderCard = (industry: CarouselIndustry, index: number) => {
    if (industry.isClone || !linkCards) {
      return (
        <div
          key={`${industry.key}-${industry.isClone ? industry.cloneSide : 'source'}-${index}`}
          className={styles.industryCard}
          aria-hidden={industry.isClone ? true : undefined}
        >
          <div className={styles.industryImageWrapper}>
            <Image
              src={industry.image}
              alt={industry.isClone ? '' : industry.alt}
              width={280}
              height={280}
              className={styles.industryImage}
              loading="lazy"
              quality={85}
              sizes="280px"
            />
          </div>
          <span className={styles.industryName}>{industry.name}</span>
        </div>
      );
    }

    return (
      <Link
        key={`${industry.key}-source-${index}`}
        href={industry.href}
        className={styles.industryCard}
        aria-label={`View ${industry.name} industry page`}
      >
        <div className={styles.industryImageWrapper}>
          <Image
            src={industry.image}
            alt={industry.alt}
            width={280}
            height={280}
            className={styles.industryImage}
            loading="lazy"
            quality={85}
            sizes="280px"
          />
        </div>
        <span className={styles.industryName}>{industry.name}</span>
      </Link>
    );
  };

  return (
    <div className={styles.carouselContainer}>
      {/* Row 1 - Scrolls Left */}
      <div 
        ref={row1Ref}
        className={styles.carouselWrapper}
        role="region"
        aria-label="Industries carousel"
      >
        <div 
          className={`${styles.carouselTrack} ${styles.scrollLeft}`}
          style={{ animationDuration: `${autoPlaySpeed}s` }}
        >
          {(variant === 'double-row' ? row1Items : singleRowItems).map(renderCard)}
        </div>
      </div>

      {/* Row 2 - Scrolls Right */}
      {variant === 'double-row' && row2Industries.length > 0 && (
        <div 
          ref={row2Ref}
          className={styles.carouselWrapper}
          role="region"
          aria-label="Industries carousel row 2"
        >
          <div 
            className={`${styles.carouselTrack} ${styles.scrollRight}`}
            style={{ animationDuration: `${autoPlaySpeed}s` }}
          >
            {row2Items.map(renderCard)}
          </div>
        </div>
      )}
    </div>
  );
};
